import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import { staffApi, Staff } from '../../services/staffService';
import { employeeApi, payrollApi, PayrollItem } from '../../services/employeeService';
import { useAuthStore } from '../../store/authStore';
import StaffScheduleDialog from '../../components/StaffScheduleDialog';
import {
  PageHeader, SectionCard, StatTile, EmptyState, LoadingState, StatusPill, PersonAvatar, PillTone,
} from '../../components/ui';
import { tokens } from '../../theme';
import {
  formatDate, formatDuration, formatMoney, formatMonthYear, formatNumber, formatPercent,
} from '../../utils/format';

/**
 * Profil pracownika — jedno miejsce, w ktorym widac wszystko o jednej osobie
 * (uwaga klienta nr 4, krok 2: "model pracownicy jest wszedzie w kilku
 * miejscach, to jest nieintuicyjne").
 *
 * Krok 1 (commit 1bf1c59) scalil rozrzucone pozycje menu w jedna sekcje ZESPOL.
 * Ten krok odwraca kierunek wchodzenia: zamiast wybierac najpierw temat
 * (Czas pracy / Wyplaty / Rentownosc), a potem osobe, wybiera sie osobe
 * i widzi jej tematy.
 *
 * Strony tematyczne zostaja tam, gdzie byly — sa zbiorcze, dotycza calego
 * zespolu i maja wlasne filtry. Profil do nich prowadzi, zamiast je duplikowac.
 */

interface DataSummary {
  clientsCount: number;
  servicesCount: number;
  servicesRevenue: number;
  productsRevenue: number;
  materialsCost: number;
  workedHours: number;
  avgVisitValue: number;
  revenuePerHour: number;
  productSharePct: number;
  totalRevenue: number;
  source: string;
}

type PayrollRow = PayrollItem & {
  period?: { year: number; month: number; status: string; approvedAt?: string | null; paidAt?: string | null };
};

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const monthDate = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return y && m ? new Date(y, m - 1, 1) : null;
};

const num = (v: unknown) => (typeof v === 'number' ? v : 0);

const SOURCE_LABEL: Record<string, { label: string; tone: PillTone }> = {
  AUTO: { label: 'Dane z modułu Sprzedaż', tone: 'info' },
  MANUAL: { label: 'Dane wprowadzone ręcznie', tone: 'neutral' },
  MIXED: { label: 'Sprzedaż i dane ręczne', tone: 'info' },
  NONE: { label: 'Brak danych sprzedaży', tone: 'neutral' },
};

const PERIOD_STATUS: Record<string, { label: string; tone: PillTone }> = {
  DRAFT: { label: 'W przygotowaniu', tone: 'neutral' },
  REVIEW: { label: 'Do sprawdzenia', tone: 'warning' },
  APPROVED: { label: 'Zatwierdzona', tone: 'info' },
  PAID: { label: 'Wypłacona', tone: 'success' },
  CANCELLED: { label: 'Anulowana', tone: 'danger' },
};

const DAY_NAMES = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

const tilesGrid = (lg: number) => ({
  display: 'grid',
  gap: 2,
  gridTemplateColumns: {
    xs: 'repeat(2, minmax(0, 1fr))',
    md: `repeat(${Math.min(lg, 3)}, minmax(0, 1fr))`,
    lg: `repeat(${lg}, minmax(0, 1fr))`,
  },
});

export const StaffProfilePage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role === 'SALON_OWNER' || role === 'SUPER_ADMIN';

  const [tab, setTab] = useState(0);
  const [staff, setStaff] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);

  // Stanowisko i lokalizacje pochodza z modulu pracowniczego, nie z kartoteki.
  // Bez nich profil nie zastapilby strony „Wyniki pracownikow".
  const [employee, setEmployee] = useState<{
    employeeProfile?: { position?: string; employmentType?: string } | null;
    locationAssignments?: { salon: { id: string; name: string } }[];
  } | null>(null);

  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState<DataSummary | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState('');

  const [payroll, setPayroll] = useState<PayrollRow[] | null>(null);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollError, setPayrollError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    staffApi
      .getById(id)
      .then((s) => {
        if (!cancelled) setStaff(s);
      })
      .catch((err: { response?: { data?: { error?: string } } }) => {
        if (!cancelled) setError(err?.response?.data?.error || 'Nie udało się wczytać pracownika');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Dane pracownicze sa dodatkiem — ich brak nie moze zablokowac profilu.
    employeeApi
      .detail(id)
      .then((e) => {
        if (!cancelled) setEmployee(e);
      })
      .catch(() => {
        if (!cancelled) setEmployee(null);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Po zapisaniu grafiku odswiezamy kartoteke, zeby podsumowanie tygodnia bylo aktualne.
  const refreshStaff = useCallback(() => {
    staffApi.getById(id).then(setStaff).catch(() => undefined);
  }, [id]);

  const loadData = useCallback(async () => {
    if (!id) return;
    const [year, m] = month.split('-').map(Number);
    if (!year || !m) return;
    setDataLoading(true);
    setDataError('');
    try {
      setData(await employeeApi.data(id, year, m));
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setDataError(msg || 'Nie udało się wczytać danych sprzedaży');
      setData(null);
    } finally {
      setDataLoading(false);
    }
  }, [id, month]);

  // Dane sprzedazy dociagamy dopiero po wejsciu w zakladke — profil otwiera sie
  // czesto tylko po to, zeby zajrzec w kartoteke.
  useEffect(() => {
    if (tab === 0) loadData();
  }, [tab, loadData]);

  // Historia rozliczen — tylko do odczytu, ta sama co w „Moich wyplatach” pracownika.
  useEffect(() => {
    if (tab !== 2 || !id || payroll) return;
    setPayrollLoading(true);
    setPayrollError('');
    payrollApi
      .history(id)
      .then((rows) => setPayroll(rows as PayrollRow[]))
      .catch((err: { response?: { data?: { error?: string } } }) => {
        setPayrollError(err?.response?.data?.error || 'Nie udało się wczytać rozliczeń');
        setPayroll([]);
      })
      .finally(() => setPayrollLoading(false));
  }, [tab, id, payroll]);

  const breadcrumbs = [{ label: 'Zespół' }, { label: 'Pracownicy', to: '/salon/staff' }];

  if (loading) {
    return (
      <Box>
        <PageHeader
          breadcrumbs={breadcrumbs}
          title={<Skeleton width={260} sx={{ maxWidth: '100%' }} />}
          description={<Skeleton width={320} sx={{ maxWidth: '100%' }} />}
        />
        <LoadingState variant="page" count={4} rows={3} />
      </Box>
    );
  }

  if (error || !staff) {
    return (
      <Box>
        <PageHeader breadcrumbs={breadcrumbs} title="Profil pracownika" />
        <SectionCard>
          <EmptyState
            icon={<PersonOffOutlinedIcon />}
            title="Nie udało się otworzyć profilu"
            description={error || 'Nie znaleziono pracownika. Mógł zostać usunięty — wróć do listy i wybierz osobę jeszcze raz.'}
            actions={<Button variant="outlined" onClick={() => navigate('/salon/staff')}>Wróć do listy pracowników</Button>}
          />
        </SectionCard>
      </Box>
    );
  }

  const fullName = `${staff.firstName} ${staff.lastName}`;
  const position = employee?.employeeProfile?.position;
  const locations = employee?.locationAssignments || [];
  const schedules = staff.schedules || [];
  const source = data ? SOURCE_LABEL[data.source] || { label: data.source, tone: 'neutral' as PillTone } : null;
  const selectedMonth = monthDate(month);

  const identity = (
    <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 1.25 }}>
      <PersonAvatar firstName={staff.firstName} lastName={staff.lastName} seed={staff.id} size={56} />
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
          <StatusPill label={staff.isActive ? 'Aktywny' : 'Nieaktywny'} tone={staff.isActive ? 'success' : 'neutral'} size="small" />
          {position && <StatusPill label={position} tone="plum" size="small" noDot />}
          {staff.specialization && <StatusPill label={staff.specialization} tone="neutral" size="small" noDot />}
          {staff.commissionRate > 0 && (
            <StatusPill label={`Prowizja ${formatPercent(staff.commissionRate, Number.isInteger(staff.commissionRate) ? 0 : 1)}`} tone="neutral" size="small" noDot />
          )}
        </Stack>
        {(staff.phone || staff.user?.email) && (
          <Typography variant="body2" sx={{ color: tokens.inkSoft, mt: 0.75, wordBreak: 'break-word' }}>
            {[staff.phone, staff.user?.email].filter(Boolean).join(' · ')}
          </Typography>
        )}
        {locations.length > 0 && (
          <Typography variant="body2" sx={{ color: tokens.muted, mt: 0.25 }}>
            {locations.length === 1 ? 'Lokalizacja' : 'Lokalizacje'}: {locations.map((l) => l.salon.name).join(', ')}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  const shortcuts = [
    { label: 'Czas pracy', desc: 'Ewidencja i sesje pracy', path: '/time/dashboard', icon: <AccessTimeIcon /> },
    { label: 'Nieobecności i L4', desc: 'Urlopy, zwolnienia, wyjścia', path: '/time/absences', icon: <EventBusyOutlinedIcon /> },
    { label: 'Rozliczenia miesięczne', desc: 'Wypłaty i ich zatwierdzanie', path: '/payroll/periods', icon: <PaymentsOutlinedIcon /> },
    { label: 'Rentowność', desc: 'Przychód kontra koszt pracownika', path: '/profitability', icon: <TrendingUpIcon /> },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={fullName}
        description={identity}
        actions={canManage ? (
          <Button variant="outlined" startIcon={<EditCalendarOutlinedIcon />} onClick={() => setScheduleOpen(true)}>
            Grafik pracy
          </Button>
        ) : undefined}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons={false}
          sx={{ borderBottom: `1px solid ${tokens.line}` }}
        >
          <Tab label="Wyniki" />
          <Tab label="Usługi i grafik" />
          <Tab label="Wypłaty" />
        </Tabs>
      </PageHeader>

      {/* ─── Sprzedaz i rentownosc ─── */}
      {tab === 0 && (
        <Stack gap={2.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'flex-end' }}
            justifyContent="space-between"
            gap={1.5}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h2" variant="h4">
                Wyniki za {selectedMonth ? formatMonthYear(selectedMonth) : 'wybrany miesiąc'}
              </Typography>
              {source && !dataLoading && (
                <StatusPill label={source.label} tone={source.tone} size="small" sx={{ mt: 0.75 }} />
              )}
            </Box>
            <TextField
              type="month"
              size="small"
              label="Miesiąc"
              InputLabelProps={{ shrink: true }}
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              sx={{ minWidth: 190, width: { xs: '100%', sm: 190 } }}
            />
          </Stack>

          {dataError && <Alert severity="error">{dataError}</Alert>}

          {dataLoading ? (
            <LoadingState variant="tiles" count={4} />
          ) : !data ? (
            !dataError && (
              <SectionCard>
                <EmptyState
                  compact
                  icon={<InsightsOutlinedIcon />}
                  title="Brak danych za ten miesiąc"
                  description="Wybierz inny miesiąc albo zarejestruj sprzedaż tej osoby w module Sprzedaż."
                />
              </SectionCard>
            )
          ) : (
            <>
              <Box sx={tilesGrid(4)}>
                <StatTile
                  tone="hero"
                  label="Obrót łącznie"
                  value={formatMoney(data.totalRevenue)}
                  hint="usługi i produkty"
                />
                <StatTile label="Sprzedaż usług" value={formatMoney(data.servicesRevenue)} />
                <StatTile label="Sprzedaż produktów" value={formatMoney(data.productsRevenue)} hint={`${formatPercent(num(data.productSharePct), Number.isInteger(num(data.productSharePct)) ? 0 : 1)} obrotu`} />
                <StatTile label="Koszt materiałów" value={formatMoney(data.materialsCost)} />
              </Box>
              <Box sx={{ ...tilesGrid(5), '& > :last-of-type': { gridColumn: { xs: '1 / -1', md: 'auto' } } }}>
                <StatTile label="Klienci" value={formatNumber(num(data.clientsCount))} />
                <StatTile label="Usługi" value={formatNumber(num(data.servicesCount))} unit="szt." />
                <StatTile label="Godziny pracy" value={formatNumber(num(data.workedHours), 1)} unit="h" />
                <StatTile label="Średnia wartość wizyty" value={formatMoney(data.avgVisitValue)} />
                <StatTile label="Obrót na godzinę" value={formatMoney(data.revenuePerHour)} />
              </Box>

              {data.source === 'NONE' && (
                <Alert severity="info">
                  Brak danych z kasy — sprzedaż rejestrowana w module Sprzedaż zasila te liczby automatycznie.
                </Alert>
              )}
            </>
          )}
        </Stack>
      )}

      {/* ─── Uslugi i grafik ─── */}
      {tab === 1 && (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, alignItems: 'start' }}>
          <SectionCard
            title="Usługi"
            subtitle="Usługi, które ta osoba wykonuje. Przypisania zmienia się na liście pracowników."
            icon={<ContentCutOutlinedIcon />}
          >
            {staff.services && staff.services.length > 0 ? (
              <Stack direction="row" gap={0.75} flexWrap="wrap">
                {staff.services.map((s) => (
                  <Chip key={s.service.id} label={s.service.name} />
                ))}
              </Stack>
            ) : (
              <EmptyState
                compact
                title="Brak przypisanych usług"
                description="Wizytę nadal można zapisać — system tylko ostrzeże."
              />
            )}
          </SectionCard>

          <SectionCard
            title="Grafik pracy"
            subtitle="Stały tydzień. Wyjątki na konkretne dni dodasz w oknie grafiku albo w kalendarzu."
            icon={<CalendarMonthOutlinedIcon />}
            padding="none"
          >
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {WEEK_ORDER.map((dow) => {
                const day = schedules.find((s) => s.dayOfWeek === dow);
                const working = Boolean(day?.isWorking);
                return (
                  <Stack
                    component="li"
                    key={dow}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ px: { xs: 2, md: 2.5 }, py: 1.25, borderBottom: `1px solid ${tokens.line}` }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: working ? tokens.ink : tokens.muted }}>
                      {DAY_NAMES[dow]}
                    </Typography>
                    {working && day ? (
                      <Typography sx={{ fontWeight: 650, fontVariantNumeric: 'tabular-nums', fontSize: '0.9375rem' }}>
                        {day.startTime}–{day.endTime}
                      </Typography>
                    ) : (
                      <Typography sx={{ color: tokens.muted, fontSize: '0.875rem' }}>Wolne</Typography>
                    )}
                  </Stack>
                );
              })}
            </Box>
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ px: { xs: 2, md: 2.5 }, py: 1.75 }}>
              {canManage && (
                <Button variant="outlined" startIcon={<EditCalendarOutlinedIcon />} onClick={() => setScheduleOpen(true)}>
                  Edytuj grafik
                </Button>
              )}
              <Button variant="text" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/salon/calendar')}>
                Otwórz kalendarz
              </Button>
            </Stack>
          </SectionCard>
        </Box>
      )}

      {/* ─── Czas pracy i wyplaty ─── */}
      {tab === 2 && (
        <Stack gap={2.5}>
          <SectionCard
            title="Rozliczenia"
            subtitle="Miesięczne wypłaty tej osoby — zatwierdza się je w Rozliczeniach miesięcznych."
            icon={<PaymentsOutlinedIcon />}
            padding="none"
          >
            {payrollLoading || payroll === null ? (
              <Box sx={{ p: 2.5 }}>
                <Skeleton height={28} />
                <Skeleton height={28} />
                <Skeleton height={28} width="70%" />
              </Box>
            ) : payrollError ? (
              <Box sx={{ p: 2.5 }}><Alert severity="error">{payrollError}</Alert></Box>
            ) : payroll.length === 0 ? (
              <EmptyState
                compact
                title="Brak rozliczeń"
                description="Rozliczenie pojawi się po wygenerowaniu wypłat za miesiąc."
              />
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 820 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Okres</TableCell>
                      <TableCell align="right">Czas rozliczeniowy</TableCell>
                      <TableCell align="right">Obrót</TableCell>
                      <TableCell align="right">Brutto</TableCell>
                      <TableCell align="right">Na konto</TableCell>
                      <TableCell align="right">Koszt pracodawcy</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payroll.map((r) => {
                      const st = r.period ? PERIOD_STATUS[r.period.status] || { label: r.period.status, tone: 'neutral' as PillTone } : null;
                      const statusDate = r.period?.paidAt
                        ? `wypłacono ${formatDate(r.period.paidAt)}`
                        : r.period?.approvedAt ? `zatwierdzono ${formatDate(r.period.approvedAt)}` : null;
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography component="span" sx={{ fontWeight: 600, fontSize: 'inherit' }}>
                              {r.period ? formatMonthYear(new Date(r.period.year, r.period.month - 1, 1)) : '—'}
                            </Typography>
                            {r.isCorrection && <StatusPill label="Korekta" tone="warning" size="small" noDot sx={{ ml: 1 }} />}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDuration(r.billableWorkMinutes)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatMoney(r.generatedRevenue, { decimals: 2 })}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatMoney(r.grossSalary, { decimals: 2 })}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: 700 }}>{formatMoney(r.netPaid, { decimals: 2 })}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatMoney(r.employerCost, { decimals: 2 })}</TableCell>
                          <TableCell>
                            {st ? <StatusPill label={st.label} tone={st.tone} /> : '—'}
                            {statusDate && (
                              <Typography variant="caption" component="div" sx={{ mt: 0.25, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                {statusDate}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>

          <SectionCard title="Widoki całego zespołu" subtitle="Ewidencja czasu i rozliczenia prowadzone są dla całego zespołu — tutaj są skróty.">
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
              {shortcuts.map((link) => (
                <Paper
                  key={link.path}
                  variant="outlined"
                  component="button"
                  type="button"
                  onClick={() => navigate(link.path)}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    textAlign: 'left',
                    font: 'inherit',
                    color: 'inherit',
                    bgcolor: tokens.paper,
                    cursor: 'pointer',
                    transition: 'border-color .15s ease, background-color .15s ease',
                    '&:hover': { borderColor: tokens.lineStrong, bgcolor: tokens.plumSofter },
                    '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 2 },
                  }}
                >
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: tokens.plumSoft, color: tokens.plum, display: 'grid', placeItems: 'center', flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: 20 } }}>
                    {link.icon}
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem' }}>{link.label}</Typography>
                    <Typography variant="caption" component="div">{link.desc}</Typography>
                  </Box>
                  <ArrowForwardIcon sx={{ fontSize: 18, color: tokens.muted }} />
                </Paper>
              ))}
            </Box>
          </SectionCard>
        </Stack>
      )}

      {canManage && (
        <StaffScheduleDialog
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          onSave={refreshStaff}
          staffId={staff.id}
          staffName={fullName}
        />
      )}
    </Box>
  );
};

export default StaffProfilePage;
